import { SagaIterator } from 'redux-saga';
import { call, put, takeEvery } from 'redux-saga/effects';

import { CreateBroadcastView, DeleteBroadcastView, GetBroadcastDetail } from '~src/bootstrap/redux/actions/AppActionTypes';
import { requestFailed } from '~src/bootstrap/redux/actions/RequestFailedAction';
import { Messages } from '~src/codegen/Localize';
import { BroadcastErrorCode } from '~src/commonInfra/utils/ErrorCodes';
import { IError } from '~src/httpClient/serverApi/types/IError';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ILongRunningAction } from '~src/permissions/services/types/ILongRunningAction';
import { IBroadcastableItemInfo } from '~src/spacesPlatform/models/resourceInfoParts';

import { broadcastViewCreated, broadcastViewDeleted, CreateBroadcastViewAction, DeleteBroadcastViewAction, GetBroadcastDetailAction, updateBroadcastView } from '../actions/BroadcastActions';
import { IBroadcastDetail } from '../models/IBroadcastContentInfo';
import { Broadcasts } from '../resources/Broadcasts';
import { IGetDetailBroadcastResult } from '../serverApi/requests/GetDetailOfABroadcastRequest';

export function* rootBroadcastSaga(): SagaIterator {
    yield takeEvery(CreateBroadcastView, handleCreateBroadcastView);
    yield takeEvery(DeleteBroadcastView, handleDeleteBroadcastView);
    yield takeEvery(GetBroadcastDetail, processGetBroadcastDetail);
}

export function* handleCreateBroadcastView(action: CreateBroadcastViewAction) {
    try {
        // asynchronous process of publishing a broadcast
        // 1. create the broadcast
        yield call(createBroadcast, action.item);
        yield put(broadcastViewCreated());
        try {
            // 2. fetch attributes of the broadcast such as embed code and public-facing URL
            const result = yield call(getBroadcastView, action.item);
            yield put(updateBroadcastView(result));
        } catch (e) {
            yield put(requestFailed(e, Messages.broadcastUnexpectedError()));
        }
    } catch (e) {
        const localizedMessage = getErrorMessage(e);
        yield put(requestFailed(e, localizedMessage));
    }
}

export function* handleDeleteBroadcastView(action: DeleteBroadcastViewAction) {
    try {
        yield call(deleteBroadcast, action.item);
        yield put(broadcastViewDeleted());
    } catch (e) {
        yield put(requestFailed(e, Messages.broadcastError()));
    }
}

export function* processGetBroadcastDetail(action: GetBroadcastDetailAction) {
    try {
        const result = yield call(getBroadcastView, action.item);
        yield put(updateBroadcastView(result));
    } catch (e) {
        const errorCode = e.code;
        if (errorCode === BroadcastErrorCode.BroadcastNotFound) {
            // update states for the initial load of broadcast
            yield put(updateBroadcastView());
        } else {
            // otherwise pop up error toast
            yield put(requestFailed(e, Messages.broadcastUnexpectedError()));
        }
    }
}

export function createBroadcast(item: IBroadcastableItemInfo): Promise<any> {
    return Broadcasts.createBroadcast(item.id, false /*showTabs*/, false /*showWatermark*/, true /*overwrite*/, true /*acceptTermsOfUse*/)
        .then(onComplete, onFail);
}

export function deleteBroadcast(item: IBroadcastableItemInfo): Promise<any> {
    let result = Broadcasts.deleteBroadcast(item.luid)
        .then((result: IResult) => {
            if (result?.errors?.length) {
                return onFail(result.errors);
            }
            return Promise.resolve(result);
        }, onFail);
    return result;
}

export function getBroadcastView(item: IBroadcastableItemInfo): Promise<IBroadcastDetail> {
    return Broadcasts.getDetailOfABroadcast(item.luid)
        .then((result: IGetDetailBroadcastResult) => {
            if (result.errors) {
                return Promise.reject(result.errors[0]);
            }
            return Promise.resolve(result as IBroadcastDetail);
        }, (reason) => Promise.reject(reason));
}

function onFail(errors: IError[]): Promise<any> {
    return Promise.reject(errors);
}

function onComplete(result: ILongRunningAction): Promise<any> {
    if (result && result.succeeded) {
        return Promise.resolve(result);
    }
    return onFail(result.response && result.response.result.errors);
}

export function getErrorMessage(errors: IError[]): string {
    if (!errors) {
        return Messages.unexpectedError();
    }

    const code: BroadcastErrorCode = errors[0]?.code;
    const metaData = errors[0]?.errorMetaData?.detailLimit;
    switch (code) {
        case BroadcastErrorCode.BroadcastErrorUnknown:
            return Messages.broadcastError();
        case BroadcastErrorCode.BroadcastWorkbookDataSourcesAreNotExtracts:
            return Messages.broadcastWorkbookDataSourcesAreNotExtracts();
        case BroadcastErrorCode.BroadcastWorkbookRowLimitExceeded:
            return Messages.broadcastWorkbookRowLimitExceeded({ ROW_LIMIT: metaData });
        case BroadcastErrorCode.BroadcastWorkbookSizeLimitExceeded:
            return Messages.broadcastWorkbookSizeLimitExceeded({ SIZE_LIMIT: metaData });
        case BroadcastErrorCode.BroadcastWorkbookContainsUserFilter:
            return Messages.broadcastWorkbookContainsUserFilter();
        case BroadcastErrorCode.BroadcastWorkbookContainsCustomSQL:
            return Messages.broadcastWorkbookContainsCustomSQL();
        case BroadcastErrorCode.BroadcastWorkbookContainsCube:
            return Messages.broadcastWorkbookContainsCube();
        case BroadcastErrorCode.BroadcastWorkbookContainsScript:
            return Messages.broadcastWorkbookContainsScript();
        case BroadcastErrorCode.BroadcastWorkbookUnknownValidationError:
            return Messages.broadcastWorkbookUnknownValidationError();
        case BroadcastErrorCode.CannotOverwriteBroadcast: // User doesn't need set overwrite flag in UI
            return Messages.broadcastServiceError();
        case BroadcastErrorCode.BroadcastNotFound:
            return Messages.broadcastNotFound();
        case BroadcastErrorCode.BroadcastServiceError:
            return Messages.broadcastServiceError();
        default:
            return Messages.broadcastError();
    }
}
